/**
 * The grid settings ⋮'s "Download as CSV" (H233) for every Premium grid — ONE implementation.
 *
 * Aaron 2026-10-09, on the aircraft tabs: "Fix export first". MUI's own CSV writes one line
 * per grid row and each cell's VALUE, so a StackedLines row (one line per limit on screen,
 * BP-106) exported only one limit, and a band / section-header row exported as data. Here:
 *
 * - a row with N limits (a `stackedColumn`, which records its `limitsOf`) exports N lines,
 *   in the order the screen lists them: the record's other columns repeat on each line,
 *   and each stacked column writes THAT limit's value, formatted as the column formats it;
 * - a row the grid's module names a band / header (`skipRow`) is not exported;
 * - everything else is MUI's rule, unchanged: MUI's own header lines (column groups
 *   included), the visible columns that are not `disableExport`, every filtered + sorted
 *   row — only the ticked rows when any are ticked — and each cell's formatted value,
 *   quoted as MUI quotes it. A grid with neither writes byte-for-byte what MUI writes
 *   (pinned: __tests__/gridCsvExport.test.tsx).
 */
import type { MutableRefObject } from "react";
import {
  gridFilteredSortedRowIdsSelector,
  gridRowTreeSelector,
  gridVisibleColumnDefinitionsSelector,
  type GridApi,
  type GridColDef,
  type GridRowId,
} from "@mui/x-data-grid-premium";

import { STACKED_LIMITS_OF, lineValue } from "./StackedLines";

type StackedLimitsOf = (row: any) => any[] | null | undefined;

export interface GridCsvOptions {
  /** The file's name, without ".csv". */
  fileName?: string;
  /** A row that is not a record (a band, a section header): not exported. */
  skipRow?: (row: any) => boolean;
}

const DELIMITER = ",";

/** MUI's own quoting (csvSerializer `sanitizeCellValue`, shouldAppendQuotes on). */
function sanitize(value: unknown): unknown {
  if (typeof value !== "string") return value;
  return [DELIMITER, "\n", "\r", '"'].some((d) => value.includes(d)) ? `"${value.replace(/"/g, '""')}"` : value;
}

const line = (values: unknown[]) =>
  values.map((v) => (v === null || v === undefined ? "" : String(sanitize(v)))).join(DELIMITER);

/** MUI's `defaultGetRowsToExport`: filtered + sorted, no footers; the ticked rows only when any are ticked. */
function rowsToExport(apiRef: MutableRefObject<GridApi>): GridRowId[] {
  const tree = gridRowTreeSelector(apiRef);
  const ids = gridFilteredSortedRowIdsSelector(apiRef).filter((id) => tree[id]?.type !== "footer");
  const selected = apiRef.current.getSelectedRows();
  return selected.size > 0 ? ids.filter((id) => selected.has(id)) : ids;
}

/** One limit's text in a stacked column: the column's value on that limit, formatted as the column formats it. */
function limitText(apiRef: MutableRefObject<GridApi>, col: GridColDef, lim: any): unknown {
  const value = lineValue(col, lim);
  return col.valueFormatter ? (col.valueFormatter as any)(value, lim, col, apiRef) : value;
}

export function buildGridCsv(apiRef: MutableRefObject<GridApi>, options: GridCsvOptions = {}): string {
  const columns = gridVisibleColumnDefinitionsSelector(apiRef).filter((c) => !c.disableExport);
  // MUI's header lines, exactly (column-group rows included): its CSV of no rows.
  const head = apiRef.current.getDataAsCsv({ getRowsToExport: () => [] });
  const body: string[] = [];
  for (const id of rowsToExport(apiRef)) {
    const row = apiRef.current.getRow(id);
    if (row && options.skipRow?.(row)) continue;
    const cell = (field: string) => apiRef.current.getCellParams(id, field).formattedValue;
    const stacked = columns.map((c) => {
      const limitsOf = (c as any)[STACKED_LIMITS_OF] as StackedLimitsOf | undefined;
      const lims = row && limitsOf ? limitsOf(row) : null;
      return lims && lims.length > 1 ? lims : null;
    });
    const lines = Math.max(1, ...stacked.map((l) => l?.length ?? 0));
    for (let i = 0; i < lines; i += 1) {
      body.push(
        line(
          columns.map((c, k) => {
            const lims = stacked[k];
            if (!lims) return cell(c.field);
            return i < lims.length ? limitText(apiRef, c, lims[i]) : "";
          })
        )
      );
    }
  }
  // MUI trims the body, then the whole file (buildCSV).
  return `${head}\r\n${body.join("\r\n").trim()}`.trim();
}

/** A file name the OS accepts: no path or reserved characters. */
export const csvFileName = (name: string) => name.replace(/[\\/:*?"<>|]+/g, "-").trim() || "Aeronet";

/** Build the CSV and hand it to the browser as `<fileName>.csv` (MUI's `exportAs`). */
export function exportGridCsv(apiRef: MutableRefObject<GridApi>, options: GridCsvOptions = {}) {
  const blob = new Blob([buildGridCsv(apiRef, options)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${csvFileName(options.fileName ?? "Aeronet")}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL?.(url));
}
